import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import { MSpinner, MSelect } from "components/Shared";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import RadioGroup from '@mui/material/RadioGroup';
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import MGridForm from "components/Shared/MGridForm";
import validator from "components/Shared/MGridFormDefinitions/validator";
import {
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_STRING,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_ZIP,
} from "typeConstants/dataTypes";
import {
  BUSINESSDEVELOPMENT_VP,
  BUSINESSDEVELOPMENT_DIRECTOR,
  BUSINESSDEVELOPMENT_MANAGER,
  SALES_INSURANCE_DIRECTOR,
  SALES_INSURANCE_MANAGER,
  SALES_INSURANCE_VICE_PRESIDENT,
  SALES_VALUATION_DIRECTOR,
  SALES_VALUATION_VICE_PRESIDENT
} from "typeConstants/employeeRoles";
import { CustomerTypeDropdownEntries } from "typeConstants/customerTypes";
import { getStateAbbreviationList } from "typeConstants/states";
import { getMajorCompanies } from "actions/customer";
import { MajorCompanyDto, CustomerAddRequest, CustomerListModel, EmployeeListDto } from "interfaces/Mueller.Business.Models";
import { getNextAvailableCustomerNumber, getSurveyTypesByCustomer } from "requests/customer";
import { MSelectOption } from "components/Shared/MSelect";
import MCheckboxGroup, { MCheckboxGroupOption, MCheckboxGroupControlMethods } from "components/Shared/MCheckboxGroup.tsx";
import { getEmployeesByRoles } from "requests/employee/index";

interface AddCustomerProps {
  onCancel: () => void;
  onSubmit: (request: CustomerAddRequest) => void;
  // Redux
  getMajorCompanies: () => void;
  majorCompanies: MajorCompanyDto[];
  customerList: CustomerListModel[];
}

const customerAddRequest: CustomerAddRequest = {
  customerNumber: null,
  name: "",
  companyId: null,
  companyName: null,
  customerTypeId: null,
  address1: "",
  address2: "",
  city: "",
  stateAbbreviation: "",
  zip: "",
  contactName: "",
  contactPhone: "",
  copyFromCustomerNumber: null,
  copySurveyTypeCodes: [],
  salesRepEmployeeId: null,
};

enum CompanyType {
  NEW = "NEW",
  EXISTING = "EXISTING",
};

const AddCustomer = (props: AddCustomerProps) => {
  const [accountNumber, setAccountNumber] = useState<number>();
  const [newCustomer, setNewCustomer] = useState<CustomerAddRequest>(customerAddRequest);
  const [majorCompanyType, setMajorCompanyType] = useState<string>(CompanyType.EXISTING);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [copySurveyTypes, setCopySurveyTypes] = useState<boolean>(false);
  const [copyFromAccount, setCopyFromAccount] = useState<MSelectOption | null>(null);
  const [accountSurveyTypes, setAccountSurveyTypes] = useState<MCheckboxGroupOption[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [salesReps, setSalesReps] = useState<MSelectOption[]>([]);
  const formControlMethods = useRef({});
  const checkboxControlMethods = useRef<MCheckboxGroupControlMethods | {}>({});
  const mounted = useRef(true);

  useEffect(() => {
    getNextAvailableCustomerNumber(
      (newNumber: number) => setAccountNumber(newNumber),
      mounted);
    if (!props.majorCompanies) {
      props.getMajorCompanies();
    }
    getEmployeesByRoles([
      BUSINESSDEVELOPMENT_VP,
      BUSINESSDEVELOPMENT_DIRECTOR,
      BUSINESSDEVELOPMENT_MANAGER,
      SALES_INSURANCE_DIRECTOR,
      SALES_INSURANCE_MANAGER,
      SALES_INSURANCE_VICE_PRESIDENT,
      SALES_VALUATION_DIRECTOR,
      SALES_VALUATION_VICE_PRESIDENT
    ],
      handleEmployeesResponse,
      mounted
    );
    return () => {
      mounted.current = false;
    };
  }, []);

  // Dialog buttons

  const handleEmployeesResponse = (r: EmployeeListDto[]) => {
    let options = r?.map(e => ({ label: e.firstLastName, value: e.employeeId }));
    setSalesReps(options);
  }

  const onAffirm = () => {
    // @ts-ignore
    const isValid = formControlMethods.current.isFormValid();
    if (currentStep === 1 && isValid) {
      setCurrentStep(2);
    } else if (isValid) {
      setSubmitting(true);
      let request = { ...newCustomer };
      // Some fields aren't populated in the request in state due to render issues
      request.customerNumber = accountNumber;
      if (copyFromAccount) {
        // @ts-ignore
        request.copyFromCustomerNumber = copyFromAccount.value;
        // @ts-ignore
        request.copySurveyTypeCodes = checkboxControlMethods.current.getChecked();
      }
      props.onSubmit(request);
    }
  };

  const onNegate = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      props.onCancel();
    }
  };

  const getAffirmButtonLabel = () => currentStep == 2 ? "Submit" : "Next";
  const getNegateButtonLabel = () => currentStep == 2 ? "Back" : "Cancel";

  // MGridForm Account Info

  const onCustomerChange = updatedObject => setNewCustomer({ ...updatedObject });

  const majorCompanyOptions = props.majorCompanies ? props.majorCompanies.map(v => ({ label: v.companyName, value: v.id })) : [];
  const stateOptions = getStateAbbreviationList().map(s => ({ label: s, value: s }));

  const memoizedFormDefinition = useMemo(
    () => {
      let mGridFormDefinition = [
        [
          { dataType: DATA_TYPE_STRING, label: "Account Name", source: "name", hasValidationError: validator.require, dataTypeOptions: null }
        ],
      ];

      if (majorCompanyType === CompanyType.EXISTING) {
        mGridFormDefinition.push(
          [
            { dataType: DATA_TYPE_DROPDOWN, label: "Company", source: "companyId", dataTypeOptions: { options: majorCompanyOptions }, hasValidationError: validator.required }
          ],
        );
      } else {
        mGridFormDefinition.push(
          [
            { dataType: DATA_TYPE_STRING, label: "Company Name", source: "companyName", hasValidationError: validator.required, dataTypeOptions: null }
          ],
        );
      }

      mGridFormDefinition.push(
        [
          { dataType: DATA_TYPE_DROPDOWN, label: "Type", source: "customerTypeId", dataTypeOptions: { options: CustomerTypeDropdownEntries }, hasValidationError: validator.required }
        ],
        [
          { dataType: DATA_TYPE_STRING, label: "Street Address 1", source: "address1", hasValidationError: validator.required, dataTypeOptions: null }
        ],
        [
          { dataType: DATA_TYPE_STRING, label: "Street Address 2", source: "address2", hasValidationError: null, dataTypeOptions: null }
        ],
        [
          { dataType: DATA_TYPE_STRING, label: "City", source: "city", hasValidationError: validator.required, dataTypeOptions: null  }
        ],
        [
          { dataType: DATA_TYPE_DROPDOWN, label: "State", source: "stateAbbreviation", dataTypeOptions: { options: stateOptions }, hasValidationError: validator.required }
        ],
        [
          { dataType: DATA_TYPE_ZIP, label: "Postal Code", source: "zip", hasValidationError: validator.required, dataTypeOptions: null  }
        ],
        [
          { dataType: DATA_TYPE_STRING, label: "Contact Name", source: "contactName", hasValidationError: validator.required, dataTypeOptions: null  }
        ],
        [
          { dataType: DATA_TYPE_PHONE_NUMBER, label: "Contact Phone", source: "contactPhone", hasValidationError: validator.phoneNumber, dataTypeOptions: null  }
        ],
        [
          { dataType: DATA_TYPE_DROPDOWN, label: "Sales Rep", source: "salesRepEmployeeId", dataTypeOptions: { options: salesReps }, hasValidationError: validator.required }
        ],
      );

      return mGridFormDefinition;
    },
    [majorCompanyType, props.majorCompanies, salesReps]
  );

  // Copy survey types

  const customerOptions = props.customerList
    ? props.customerList.map(c => ({ value: c.number, label: `${c.number} - ${c.name}` }))
    : [];

  const onAccountChange = (option: MSelectOption) => {
    option ? setCopyFromAccount(option) : setCopyFromAccount(null);

    getSurveyTypesByCustomer(
      option.value,
      (response) => setAccountSurveyTypes(response.map(s => ({ label: s.name, value: s.code, defaultChecked: false }))),
      mounted
    );
  };

  // Render

  if (submitting || !props.majorCompanies || !salesReps) {
    return (<MSpinner />);
  }

  return (
    <div className="add-customer-account">
      {currentStep === 1 && <>
        <div className="field-container">{`New Account Number: ${accountNumber ? accountNumber : "(waiting...)"}`}</div>
        <div className="field-container">
          <></>
          <span>Select Major Company Type:</span>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="major company type"
              name="majorCompanyType"
              value={majorCompanyType}
              onChange={e => setMajorCompanyType(e.target.value)}>
              <FormControlLabel value={CompanyType.NEW} control={<Radio />} label="New" />
              <FormControlLabel value={CompanyType.EXISTING} control={<Radio />} label="Existing" />
            </RadioGroup>
          </FormControl>
        </div>
        <MGridForm
          width={500}
          data={newCustomer}
          onChange={onCustomerChange}
          formDefinition={memoizedFormDefinition}
          portalTarget={() => document.querySelector(".add-customer-dialog")}
          getControlMethods={cm => { formControlMethods.current = cm; }}
        />
      </>}
      {currentStep === 2 && <>
        <FormControl component="fieldset">
          <FormGroup aria-label="position" row>
            <FormControlLabel
              style={{ fontSize: "12px" }}
              value="top"
              control={
                <Checkbox
                  color="default"
                  checked={copySurveyTypes}
                  value={copySurveyTypes}
                  onChange={() => setCopySurveyTypes(!copySurveyTypes)}
                />}
              label="Copy Survey Types from Existing Account"
              labelPlacement="end"
            />
          </FormGroup>
        </FormControl>
        <div className={copySurveyTypes ? "show-copy-controls" : "hide-copy-controls"}>
          <MSelect
            id="selectAccount"
            placeholder="Select account:"
            className="md-full-width"
            options={customerOptions}
            value={copyFromAccount}
            onChange={onAccountChange}
            name="Notification"
            getMenuPortalTarget={() => document.querySelector(".add-customer-dialog")}
          />
          {copyFromAccount && <MCheckboxGroup
            controlMethods={cm => checkboxControlMethods.current = cm}
            fields={accountSurveyTypes}
            cellSize={6}
            containerClassName={"survey-types-checkbox-container"}
          />}

        </div>
      </>}
      <div className="button-row-margin">
        <Button
          onClick={onNegate}
        >{getNegateButtonLabel()}</Button>
        <Button
          onClick={onAffirm}
        >{getAffirmButtonLabel()}</Button>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  majorCompanies: state.customer.majorCompanies,
  customerList: state.customer.customerList, // This is fetched in the parent component CustomerList.tsx
});

const mapDispatchToProps = dispatch => ({
  getMajorCompanies() {
    dispatch(getMajorCompanies());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AddCustomer);